import { useCallback, useEffect, useRef, useState } from 'react';
import { BodyDiagram } from '../components/BodyDiagram';
import { ExerciseDetail, type ExerciseDetailData } from '../components/ExerciseDetail';
import { ExercisePicker } from '../components/ExercisePicker';
import { Heatmap } from '../components/Heatmap';
import { PageLayout } from '../components/ManageLayout';
import { PRTable } from '../components/PRTable';
import { RecentSessions } from '../components/RecentSessions';
import { RegionBalance } from '../components/RegionBalance';
import { SessionsByMonth } from '../components/SessionsByMonth';
import { TopExercises } from '../components/TopExercises';
import css from './Dashboard.module.css';

interface Overview {
  totalSessions: number;
  totalWorkingSets: number;
  totalTonnageKg: number;
  totalCardioKm: number;
  totalCardioHours: number;
  totalExercises: number;
  totalMuscleGroups: number;
  dateRange: { earliest: string | null; latest: string | null };
}

interface SessionsMonthRow {
  month: string;
  source: string;
  count: number;
}

interface CalendarRow {
  date: string;
  sessions: number;
  sets: number;
}

interface RecentSessionRow {
  id: number;
  date: string;
  source: string;
  mode: string;
  day_id: string | null;
  day_label: string | null;
  active_exercise: string | null;
  notes: string | null;
  started_at: number;
  ended_at: number | null;
  set_count: number;
  exercise_count: number;
}

interface TopExerciseRow {
  exercise_id: string;
  name: string | null;
  region: string | null;
  sets: number;
  sessions: number;
  last_seen: string;
}

interface ExerciseListRow {
  id: string;
  name: string | null;
  region: string | null;
  sets: number;
}

interface CardioRow {
  id: number;
  date: string;
  source: string;
  notes: string | null;
  active_exercise: string | null;
  started_at: number;
  ended_at: number | null;
  media_path: string;
  duration_sec: number | null;
  distance_m: number | null;
  speed_kmh: number | null;
  avg_hr_bpm: number | null;
  peak_hr_bpm: number | null;
  elevation_gain_m: number | null;
}

interface PRRow {
  exercise_id: string;
  name: string | null;
  region: string | null;
  total_sets: number;
  heaviest_kg: number | null;
  heaviest_reps: number | null;
  heaviest_date: string | null;
  best_1rm: number | null;
  best_1rm_kg: number | null;
  best_1rm_reps: number | null;
  best_1rm_date: string | null;
}

const WINDOWS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
  { label: 'all', days: 365 * 30 },
];
const DEFAULT_DAYS = 30;

function useJson<T>(url: string | null): T | null {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    if (!url) {
      setData(null);
      return;
    }
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return data;
}

export function Dashboard() {
  const overview = useJson<Overview>('/api/stats/overview');
  const sessionsMonth = useJson<SessionsMonthRow[]>('/api/stats/sessions-per-month');
  const calendar = useJson<CalendarRow[]>('/api/stats/activity-calendar');
  const recent = useJson<RecentSessionRow[]>('/api/stats/recent-sessions?limit=12');
  const top = useJson<TopExerciseRow[]>('/api/stats/top-exercises?limit=10');
  const exercises = useJson<ExerciseListRow[]>('/api/stats/exercises');
  const prs = useJson<PRRow[]>('/api/stats/prs?min_sets=3');
  const cardio = useJson<CardioRow[]>('/api/stats/cardio-sessions?limit=50');
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  // Shared time window for the Region-balance + Muscle-activation panels.
  const [days, setDays] = useState<number>(DEFAULT_DAYS);
  const drillDownRef = useRef<HTMLDivElement>(null);
  const detail = useJson<ExerciseDetailData>(
    selectedExercise ? `/api/stats/exercise/${encodeURIComponent(selectedExercise)}` : null,
  );

  const fetchRegions = useCallback(async (days: number) => {
    const r = await fetch(`/api/stats/region-balance?days=${days}`);
    if (!r.ok) return null;
    return (await r.json()) as { regions: { region: string; sets: number; sessions: number }[] };
  }, []);

  const fetchMuscles = useCallback(async (days: number) => {
    const r = await fetch(`/api/stats/muscle-activation?days=${days}`);
    if (!r.ok) return null;
    return (await r.json()) as {
      muscles: {
        muscle_id: string;
        name: string;
        sets_primary: number;
        sets_secondary: number;
        sets_total: number;
        tonnage_current: number;
        tonnage_peak: number;
        ratio: number;
      }[];
    };
  }, []);

  useEffect(() => {
    if (!selectedExercise && exercises && exercises.length > 0) {
      setSelectedExercise(exercises[0]?.id ?? null);
    }
  }, [exercises, selectedExercise]);

  function jumpToDrillDown(exerciseId: string) {
    setSelectedExercise(exerciseId);
    drillDownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (!overview) {
    return <div className={css.loading}>Loading…</div>;
  }

  const yearsCovered = overview.dateRange.earliest
    ? Math.floor(
        (Date.parse(overview.dateRange.latest ?? '') -
          Date.parse(overview.dateRange.earliest)) /
          (365.25 * 86400 * 1000),
      )
    : 0;

  return (
    <PageLayout>
      {overview.dateRange.earliest && (
        <p className={css.range}>
          {overview.dateRange.earliest} → {overview.dateRange.latest}
          <span className={css.years}>  ·  {yearsCovered} years of history</span>
        </p>
      )}

      <section className={css.cards}>
        <Card label="Sessions" value={fmt(overview.totalSessions)} />
        <Card label="Working sets" value={fmt(overview.totalWorkingSets)} />
        <Card label="Tonnage" value={`${fmt(overview.totalTonnageKg)} kg`} />
        <Card
          label="Cardio"
          value={`${fmt(overview.totalCardioKm)} km`}
          sub={`${overview.totalCardioHours} hr`}
        />
        <Card label="Exercises" value={fmt(overview.totalExercises)} />
        <Card label="Muscle groups" value={fmt(overview.totalMuscleGroups)} />
      </section>

      <Section title="Activity">
        {calendar ? <Heatmap data={calendar} monthsBack="all" /> : <Skeleton h={160} />}
      </Section>

      <Section title="Sessions per month (by source)">
        {sessionsMonth ? <SessionsByMonth data={sessionsMonth} /> : <Skeleton h={280} />}
      </Section>

      <Section title="Recent sessions">
        {recent ? <RecentSessions data={recent} /> : <Skeleton h={300} />}
      </Section>

      <section className={css.balanceBlock}>
        <div className={css.balanceHeader}>
          <h2 className={css.sectionTitle}>Region balance + muscle activation</h2>
          <div className={css.windowSel}>
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                type="button"
                className={`${css.windowBtn}${w.days === days ? ` ${css.windowBtnActive}` : ''}`}
                onClick={() => setDays(w.days)}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <div className={css.twoCol}>
          <div className={css.sectionBody}>
            <RegionBalance days={days} fetchFor={fetchRegions} />
          </div>
          <div className={css.sectionBody}>
            <BodyDiagram days={days} fetchFor={fetchMuscles} />
          </div>
        </div>
      </section>

      <Section title="Personal records">
        {prs ? <PRTable data={prs} onSelect={jumpToDrillDown} /> : <Skeleton h={400} />}
      </Section>

      <Section title="Top 10 exercises by sets">
        {top ? <TopExercises data={top} /> : <Skeleton h={300} />}
      </Section>

      <div ref={drillDownRef}>
        <Section title="Exercise drill-down">
          {exercises ? (
            <div className={css.drillDown}>
              <ExercisePicker
                exercises={exercises}
                selectedId={selectedExercise}
                onSelect={setSelectedExercise}
              />
              {detail ? (
                <div className={css.drillDownBody}>
                  <ExerciseDetail data={detail} />
                </div>
              ) : selectedExercise ? (
                <Skeleton h={400} />
              ) : (
                <p className={css.hint}>pick an exercise above</p>
              )}
            </div>
          ) : (
            <Skeleton h={120} />
          )}
        </Section>
      </div>
    </PageLayout>
  );
}

function Card({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className={css.card}>
      <div className={css.cardLabel}>{label}</div>
      <div className={css.cardValue}>{value}</div>
      {sub && <div className={css.cardSub}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={css.section}>
      <h2 className={css.sectionTitle}>{title}</h2>
      <div className={css.sectionBody}>{children}</div>
    </section>
  );
}

function Skeleton({ h }: { h: number }) {
  return <div className={css.skeleton} style={{ height: h }} />;
}

function fmt(n: number): string {
  return n.toLocaleString('en-US');
}
